import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2, UploadCloud, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../lib/axios';
import { euro, dateFmt } from '../lib/format';
import { useImportJobStore } from '../stores/importJobStore';
import { useIsSuperAdmin } from '../stores/authStore';
import Spinner from '../components/Spinner';

type EditLine = {
  id: number;
  operationDate: string;
  bankLabel: string;
  amount: number;
  aiSuggestedAccount: string;
  aiSuggestedLabel: string;
  aiSuggestedParty: string | null;
  aiConfidence: number | null;
  ignored: boolean;
  matched: boolean;
  matchedEntryId: number | null;
  matchScore: number | null;
  matchSummary: string | null;
  learned: boolean;
  cashDeposit: boolean;
  paymentMode: string;
  posted: boolean;
};

// Payment modes offered per statement line. Selecting CASH_DEPOSIT reveals the
// "create the cash deposit" checkbox (checked by default) and books to account 531.
const PAYMENT_MODES = ['TRANSFER', 'DIRECT_DEBIT', 'CARD', 'CHEQUE', 'CASH_DEPOSIT'] as const;
const CASH_ACCOUNT = '531';

type LineGroup = { key: string; sample: string; ids: number[]; total: number; account: string; label: string };

// A user rule that pulls extra lines into a group by their bank label.
type CaptureRule = { mode: 'startsWith' | 'contains'; text: string };

// Accent-insensitive, upper-cased label used for capture-rule matching.
const normLabel = (s: string) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();

const matchesRule = (label: string, r: CaptureRule) => {
  const l = normLabel(label);
  const t = normLabel(r.text);
  if (!t) return false;
  return r.mode === 'startsWith' ? l.startsWith(t) : l.includes(t);
};

// Maps a server statement line to the editable table row.
const mapLine = (l: any): EditLine => ({
  id: l.id,
  operationDate: l.operationDate,
  bankLabel: l.bankLabel,
  amount: l.amount,
  aiSuggestedAccount: l.aiSuggestedAccount ?? '',
  aiSuggestedLabel: l.aiSuggestedLabel ?? '',
  aiSuggestedParty: l.aiSuggestedParty ?? null,
  aiConfidence: l.aiConfidence,
  ignored: l.status === 'IGNORED',
  matched: l.status === 'MATCHED',
  matchedEntryId: l.matchedEntryId ?? null,
  matchScore: l.matchScore ?? null,
  matchSummary: l.matchSummary ?? null,
  learned: typeof l.aiReasoning === 'string' && l.aiReasoning.startsWith('Learned rule'),
  cashDeposit: !!l.cashDeposit,
  paymentMode: l.paymentMode ?? (l.cashDeposit ? 'CASH_DEPOSIT' : ''),
  // A line already turned into an accounting entry (or a reconciliation that has been linked).
  posted: l.status === 'VALIDATED' || l.entryId != null,
});

// Normalizes a bank label into a grouping key: accents, digits and dates are dropped so
// repeated operations ("FRAIS PRLV SEPA CYBER 12/03") collapse into a single group.
const groupKey = (label: string) =>
  (label || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[0-9]/g, ' ')
    .replace(/[^A-Z ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Inline editor to add/remove capture rules on a group ("starts with" / "contains").
function CaptureRuleEditor({
  rules,
  onAdd,
  onRemove,
}: {
  rules: CaptureRule[];
  onAdd: (mode: CaptureRule['mode'], text: string) => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<CaptureRule['mode']>('contains');
  const [text, setText] = useState('');
  const submit = () => {
    const clean = text.trim();
    if (!clean) return;
    onAdd(mode, clean);
    setText('');
  };
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-brand">{t('imports.captureTitle')}</p>
      <p className="text-xs text-ink-muted">{t('imports.captureHint')}</p>
      {rules.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {rules.map((r, i) => (
            <span
              key={`${r.mode}-${r.text}-${i}`}
              className="inline-flex items-center gap-1 rounded-md bg-brand-tint px-2 py-0.5 text-xs font-medium text-brand"
            >
              {t(r.mode === 'startsWith' ? 'imports.captureStartsWith' : 'imports.captureContains')}
              {' '}
              <span className="font-semibold">{r.text}</span>
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="ml-0.5 text-brand/70 hover:text-brand"
                aria-label={t('common.delete')}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as CaptureRule['mode'])}
          className="select !w-auto text-sm"
        >
          <option value="contains">{t('imports.captureContains')}</option>
          <option value="startsWith">{t('imports.captureStartsWith')}</option>
        </select>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={t('imports.capturePlaceholder')}
          className="input min-w-[12rem] flex-1 text-sm"
        />
        <button type="button" onClick={submit} disabled={!text.trim()} className="btn btn-secondary !py-1">
          {t('imports.captureAdd')}
        </button>
      </div>
    </div>
  );
}

// Automatic credit/debit coherence check of a statement import.
type Verification = {
  lineCount: number;
  totalCredit: number;
  totalDebit: number;
  net: number;
  closingBalance: number | null;
  openingBalance: number | null;
  duplicateCount: number;
  bookedMovement: number;
  bookedGap: number;
  balanced: boolean;
  statementTotalCredit: number | null;
  statementTotalDebit: number | null;
  statementOpeningBalance: number | null;
  creditMatches: boolean;
  debitMatches: boolean;
  openingMatches: boolean;
  creditGap: number | null;
  debitGap: number | null;
  openingGap: number | null;
  signFlipLineId: number | null;
  phantomEntryCount: number;
  draftEntryCount: number;
  phantomEntryIds: number[];
  draftEntryIds: number[];
};

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'income' | 'expense' }) {
  const color = tone === 'income' ? 'text-income' : tone === 'expense' ? 'text-expense' : 'text-ink';
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2">
      <p className="text-xs text-ink-muted">{label}</p>
      <p className={`tnum font-display text-base font-semibold ${color}`}>{value}</p>
    </div>
  );
}

// Totals + coherence panel shown above the review table: credit/debit sums, the deduced opening
// balance, and the gap between the statement net movement and what has already been booked.
function VerificationPanel({ v }: { v: Verification }) {
  const { t } = useTranslation();
  const gap = Number(v.bookedGap) || 0;
  const hasGap = Math.abs(gap) >= 0.01;
  const booked = Number(v.bookedMovement) || 0;
  const anyBooked = Math.abs(booked) >= 0.01;
  const hasAnchors =
    v.statementTotalCredit != null || v.statementTotalDebit != null || v.statementOpeningBalance != null;
  const anchorsOk = v.creditMatches && v.debitMatches && v.openingMatches;
  const hasAnomaly =
    v.signFlipLineId != null || v.phantomEntryCount > 0 || v.draftEntryCount > 0;
  return (
    <div className="mb-4 rounded-xl border border-line p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-display text-sm font-semibold">{t('imports.verify.title')}</span>
        {v.duplicateCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-md bg-expense-tint px-2 py-0.5 text-xs font-semibold text-expense">
            <AlertTriangle size={13} /> {t('imports.verify.duplicates', { count: v.duplicateCount })}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label={t('imports.verify.credits')} value={euro(v.totalCredit)} tone="income" />
        <Stat label={t('imports.verify.debits')} value={euro(v.totalDebit)} tone="expense" />
        <Stat label={t('imports.verify.net')} value={euro(v.net)} />
        {v.openingBalance != null && (
          <Stat label={t('imports.verify.opening')} value={euro(v.openingBalance)} />
        )}
        {v.closingBalance != null && (
          <Stat label={t('imports.verify.closing')} value={euro(v.closingBalance)} />
        )}
      </div>

      {hasAnchors && (
        <div className="mt-3 rounded-lg border border-line">
          <div className="flex items-center gap-2 border-b border-line px-3 py-2">
            {anchorsOk ? (
              <CheckCircle2 size={15} className="text-income" />
            ) : (
              <AlertTriangle size={15} className="text-expense" />
            )}
            <span className="text-sm font-semibold">{t('imports.verify.anchorsTitle')}</span>
          </div>
          <div className="divide-y divide-line">
            {v.statementTotalCredit != null && (
              <AnchorRow
                label={t('imports.verify.credits')}
                statement={v.statementTotalCredit}
                computed={v.totalCredit}
                ok={v.creditMatches}
                gap={v.creditGap}
              />
            )}
            {v.statementTotalDebit != null && (
              <AnchorRow
                label={t('imports.verify.debits')}
                statement={v.statementTotalDebit}
                computed={v.totalDebit}
                ok={v.debitMatches}
                gap={v.debitGap}
              />
            )}
            {v.statementOpeningBalance != null && (
              <AnchorRow
                label={t('imports.verify.openingStatement')}
                statement={v.statementOpeningBalance}
                computed={v.openingBalance ?? 0}
                ok={v.openingMatches}
                gap={v.openingGap}
              />
            )}
          </div>
        </div>
      )}

      {hasAnomaly && (
        <div className="mt-3 space-y-2">
          {v.signFlipLineId != null && (
            <div className="flex items-center gap-2 rounded-lg bg-gold-tint/50 px-3 py-2 text-sm text-gold">
              <AlertTriangle size={15} />
              <span className="font-medium">{t('imports.verify.signFlip')}</span>
            </div>
          )}
          {v.phantomEntryCount > 0 && (
            <div className="flex items-center gap-2 rounded-lg bg-expense-tint/50 px-3 py-2 text-sm text-expense">
              <AlertTriangle size={15} />
              <span className="font-medium">
                {t('imports.verify.phantom', { count: v.phantomEntryCount })}
              </span>
            </div>
          )}
          {v.draftEntryCount > 0 && (
            <div className="flex items-center gap-2 rounded-lg bg-expense-tint/50 px-3 py-2 text-sm text-expense">
              <AlertTriangle size={15} />
              <span className="font-medium">
                {t('imports.verify.draft', { count: v.draftEntryCount })}
              </span>
            </div>
          )}
        </div>
      )}

      {anyBooked && (
        <div
          className={`mt-3 flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-sm ${
            hasGap ? 'bg-expense-tint/50 text-expense' : 'bg-income-tint/50 text-income'
          }`}
        >
          {hasGap ? <AlertTriangle size={15} /> : <CheckCircle2 size={15} />}
          <span className="font-medium">
            {hasGap ? t('imports.verify.gapWarn', { amount: euro(gap) }) : t('imports.verify.ok')}
          </span>
          <span className="text-ink-muted">
            {t('imports.verify.bookedDetail', { booked: euro(v.bookedMovement), net: euro(v.net) })}
          </span>
        </div>
      )}
      <p className="mt-2 text-xs text-ink-muted">{t('imports.verify.lineCount', { count: v.lineCount })}</p>
    </div>
  );
}

// One statement-footer anchor compared against the computed value, with a match/mismatch marker.
function AnchorRow({
  label,
  statement,
  computed,
  ok,
  gap,
}: {
  label: string;
  statement: number;
  computed: number;
  ok: boolean;
  gap: number | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
      <span className="flex items-center gap-2">
        {ok ? (
          <CheckCircle2 size={14} className="text-income" />
        ) : (
          <AlertTriangle size={14} className="text-expense" />
        )}
        <span className="font-medium">{label}</span>
      </span>
      <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 tnum text-ink-muted">
        <span>{t('imports.verify.statementValue', { value: euro(statement) })}</span>
        <span>{t('imports.verify.computedValue', { value: euro(computed) })}</span>
        {!ok && gap != null && (
          <span className="font-semibold text-expense">
            {t('imports.verify.gap', { amount: euro(gap) })}
          </span>
        )}
      </span>
    </div>
  );
}

export default function Imports() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [bankAccount, setBankAccount] = useState<string>('');
  const [importId, setImportId] = useState<number | null>(null);
  const [editLines, setEditLines] = useState<EditLine[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Post the generated entries immediately instead of leaving them as drafts. On by default.
  const [autoValidate, setAutoValidate] = useState(true);
  // Reconcile every created entry against the statement in the bank reconciliation. On by default.
  const [autoReconcile, setAutoReconcile] = useState(true);
  const { job, start, clear } = useImportJobStore();
  const analyzing = job?.status === 'analyzing';

  const journals = useQuery({
    queryKey: ['journals'],
    queryFn: () => api.get('/journals').then((r) => r.data),
  });

  // Bank statements are always booked to the bank journal.
  const bankJournalId = useMemo(() => {
    const bank = (journals.data ?? []).find((j: any) => j.type === 'BANK');
    return bank ? String(bank.id) : '';
  }, [journals.data]);

  const treasuryAccounts = useQuery<any[]>({
    queryKey: ['treasury-accounts'],
    queryFn: () => api.get('/treasury-accounts').then((r) => r.data),
  });

  const bankAccounts = useMemo(
    () => (treasuryAccounts.data ?? []).filter((a) => a.kind === 'BANK'),
    [treasuryAccounts.data],
  );

  // Bank account created when the fiscal year was set up: the default treasury bank
  // account, else the only one. Entries are always booked to it (not the raw 512).
  const defaultBankNumber = useMemo(() => {
    if (bankAccounts.length === 0) return '';
    return (bankAccounts.find((a) => a.defaultAccount) ?? bankAccounts[0]).accountNumber as string;
  }, [bankAccounts]);

  // Keep the selector aligned with the resolved default bank account.
  useEffect(() => {
    if (!bankAccount && defaultBankNumber) {
      setBankAccount(defaultBankNumber);
    }
  }, [defaultBankNumber, bankAccount]);

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get('/accounts').then((r) => r.data),
  });

  const history = useQuery({
    queryKey: ['imports'],
    queryFn: () => api.get('/imports/statements').then((r) => r.data),
  });

  const detail = useQuery({
    queryKey: ['import-detail', importId],
    queryFn: () => api.get(`/imports/statements/${importId}`).then((r) => r.data),
    enabled: importId != null,
  });

  // AI cost of this import - visible to platform super admins only.
  const isSuperAdmin = useIsSuperAdmin();
  const aiCost = useQuery<{ reference: string; costUsd: string }>({
    queryKey: ['ai-cost', importId],
    queryFn: () =>
      api
        .get('/ai-usage/reference', { params: { ref: `IMPORT:${importId}` } })
        .then((r) => r.data),
    enabled: isSuperAdmin && importId != null,
  });

  // Sync the editable table whenever the import detail loads - but never clobber
  // edits the user hasn't saved yet (dirty), otherwise their work is lost.
  useEffect(() => {
    if (dirty) return;
    const lines = detail.data?.lines ?? [];
    const mapped: EditLine[] = lines.map(mapLine);
    setEditLines(mapped);
    // Pre-select every line still to book (not ignored, not already posted); matched lines
    // reconcile without a checkbox so they are handled at generation time regardless.
    setSelectedIds(new Set(mapped.filter((l) => !l.ignored && !l.posted && !l.matched).map((l) => l.id)));
  }, [detail.data, dirty]);

  // A freshly opened import starts clean.
  useEffect(() => {
    setDirty(false);
    setSaveError(null);
  }, [importId]);

  // Load the suggestions table once the background analysis finishes.
  useEffect(() => {
    if (job?.status === 'done' && job.importId) {
      setImportId(job.importId);
    }
  }, [job?.status, job?.importId]);

  const lineStatus = (l: EditLine) =>
    l.ignored ? 'IGNORED' : l.posted ? (l.matched ? 'MATCHED' : 'VALIDATED') : l.matched ? 'MATCHED' : 'SUGGESTED';

  const linePayload = (l: EditLine) => ({
    id: l.id,
    amount: l.amount,
    aiSuggestedAccount: l.aiSuggestedAccount,
    aiSuggestedLabel: l.aiSuggestedLabel,
    aiSuggestedParty: l.aiSuggestedParty,
    status: lineStatus(l),
    matchedEntryId: l.matched ? l.matchedEntryId : null,
    cashDeposit: l.cashDeposit,
    paymentMode: l.paymentMode || null,
  });

  // Only pending lines are ever sent back; posted lines are already booked and immutable.
  const editablePayload = () => editLines.filter((l) => !l.posted).map(linePayload);

  const saveLines = useMutation({
    mutationFn: () =>
      api
        .put(`/imports/statements/${importId}/lines`, editablePayload())
        .then((r) => r.data),
    onSuccess: (saved: any[]) => {
      // Reflect the persisted rows straight from the response so nothing is lost.
      setEditLines(saved.map(mapLine));
      setDirty(false);
      setSaveError(null);
      qc.setQueryData(['import-detail', importId], (prev: any) =>
        prev ? { ...prev, lines: saved } : prev,
      );
    },
    onError: (e: any) =>
      setSaveError(e?.response?.data?.message ?? t('imports.saveError')),
  });

  const validate = useMutation({
    mutationFn: async () => {
      // Persist edits before creating entries.
      await api.put(`/imports/statements/${importId}/lines`, editablePayload());
      setDirty(false);
      // Merged groups apply only in the grouped view, and only when checked with an account.
      const merges = grouped
        ? groups
            .filter((g) => mergedKeys.has(g.key) && g.account)
            .map((g) => ({ lineIds: g.ids, accountNumber: g.account, label: g.label || g.sample }))
        : [];
      // Generate only the lines picked for this batch (plus matched lines, which reconcile).
      const lineIds = editLines
        .filter((l) => !l.ignored && !l.posted && (l.matched || selectedIds.has(l.id)))
        .map((l) => l.id);
      return api
        .post(`/imports/statements/${importId}/validate`, { lineIds, merges, autoValidate, autoReconcile })
        .then((r) => r.data);
    },
    onSuccess: (res: any) => {
      setSaveError(null);
      if (Array.isArray(res?.lines)) {
        const mapped = res.lines.map(mapLine);
        setEditLines(mapped);
        setSelectedIds(
          new Set(mapped.filter((l: EditLine) => !l.ignored && !l.posted && !l.matched).map((l: EditLine) => l.id)),
        );
      }
      qc.invalidateQueries({ queryKey: ['import-detail', importId] });
      qc.invalidateQueries({ queryKey: ['entries'] });
      qc.invalidateQueries({ queryKey: ['imports'] });
    },
    onError: (e: any) =>
      setSaveError(e?.response?.data?.message ?? t('imports.saveError')),
  });

  // Reactivating a previously hidden account when it is chosen for a line.
  const reactivateAccount = useMutation({
    mutationFn: (id: number) =>
      api.post(`/accounts/${id}/reactivate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });

  const removeImport = useMutation({
    mutationFn: ({ id, deleteEntries }: { id: number; deleteEntries: boolean }) =>
      api
        .delete(`/imports/statements/${id}`, { params: { deleteEntries } })
        .then((r) => r.data),
    onSuccess: (_d, { id }) => {
      setImportId((cur) => (cur === id ? null : cur));
      setDeleteTarget(null);
      qc.invalidateQueries({ queryKey: ['imports'] });
    },
  });

  // Statement pending deletion: we first read how many entries it generated so the user decides
  // explicitly whether to keep or also delete them.
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    fileName: string;
    count: number | null;
  } | null>(null);

  const askDelete = async (id: number, fileName: string) => {
    setDeleteTarget({ id, fileName, count: null });
    try {
      const { data } = await api.get(`/imports/statements/${id}/generated-entries`);
      setDeleteTarget({ id, fileName, count: data?.count ?? 0 });
    } catch {
      setDeleteTarget({ id, fileName, count: 0 });
    }
  };

  // Discards a single statement line (e.g. a duplicate) so it never generates an entry.
  const removeLine = useMutation({
    mutationFn: ({ iid, lineId }: { iid: number; lineId: number }) =>
      api.delete(`/imports/statements/${iid}/lines/${lineId}`).then((r) => r.data),
    onSuccess: (data: any) => {
      const lines = data?.lines ?? [];
      setEditLines(lines.map(mapLine));
      qc.setQueryData(['import-detail', importId], (prev: any) =>
        prev ? { ...prev, lines } : prev,
      );
    },
    onError: (e: any) =>
      setSaveError(e?.response?.data?.message ?? t('imports.saveError')),
  });

  const deleteLine = (lineId: number) => {
    if (importId && window.confirm(t('imports.deleteLineConfirm'))) {
      removeLine.mutate({ iid: importId, lineId });
    }
  };

  const patchLine = (id: number, patch: Partial<EditLine>) =>
    setEditLines((prev) => {
      setDirty(true);
      return prev.map((l) => (l.id === id ? { ...l, ...patch } : l));
    });

  // Opens the original uploaded statement file in a new tab (authenticated blob fetch).
  const openStatementFile = async (id: number) => {
    try {
      const res = await api.get(`/imports/statements/${id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      window.alert(t('imports.noFile'));
    }
  };
  // so imports can book to a disabled account the user deliberately picked.
  const chooseAccount = (id: number, number: string) => {
    patchLine(id, { aiSuggestedAccount: number });
    const acc = (accounts.data ?? []).find((a: any) => a.number === number);
    if (acc && acc.active === false) {
      reactivateAccount.mutate(acc.id);
    }
  };

  // Cash-deposit mode books the line to account 531 and pre-checks the deposit creation;
  // any other mode clears the flag and hides the checkbox.
  const changePaymentMode = (l: EditLine, mode: string) =>
    patchLine(
      l.id,
      mode === 'CASH_DEPOSIT'
        ? { paymentMode: mode, cashDeposit: true, aiSuggestedAccount: CASH_ACCOUNT }
        : { paymentMode: mode, cashDeposit: false },
    );

  const [grouped, setGrouped] = useState(false);
  const [mergedKeys, setMergedKeys] = useState<Set<string>>(new Set());
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  // Per-group capture rules: pull extra lines into a group by "starts with" / "contains".
  const [captureRules, setCaptureRules] = useState<Record<string, CaptureRule[]>>({});

  const alreadyValidated = detail.data?.import?.status === 'COMPLETED';

  const accountLabelOf = (num: string) => {
    const a = (accounts.data ?? []).find((x: any) => x.number === num);
    return a ? a.label : '';
  };

  // Account options for the datalist: active accounts only by default, or every account
  // (including hidden ones) when the user expands with the "+" toggle.
  const visibleAccounts = useMemo(() => {
    const all = (accounts.data ?? []) as any[];
    return showAllAccounts ? all : all.filter((a) => a.active !== false);
  }, [accounts.data, showAllAccounts]);

  // Lines eligible for the "create the entry" checkbox: not reconciled, not already posted,
  // not ignored as noise.
  const selectableLines = useMemo(
    () => editLines.filter((l) => !l.matched && !l.posted && !l.ignored),
    [editLines],
  );
  const allSelected =
    selectableLines.length > 0 && selectableLines.every((l) => selectedIds.has(l.id));

  // Statement lines the bank (or parser) produced more than once inside this same import: same date,
  // label and amount. Highlighted so the user can spot and delete the extra copies before booking.
  const duplicateIds = useMemo(() => {
    const sig = (l: EditLine) =>
      `${l.operationDate}|${(l.bankLabel ?? '').trim().toLowerCase()}|${Number(l.amount)}`;
    const counts = new Map<string, number>();
    for (const l of editLines) counts.set(sig(l), (counts.get(sig(l)) ?? 0) + 1);
    return new Set(editLines.filter((l) => (counts.get(sig(l)) ?? 0) > 1).map((l) => l.id));
  }, [editLines]);

  // Line the verification flagged as a probable wrong-sign booking (opposite credit/debit gaps).
  const signFlipId: number | null = detail.data?.verification?.signFlipLineId ?? null;

  // Toggles the batch selection for a single line (no persistence, no dirty flag).
  const toggleSelect = (id: number, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  // Selects or clears every selectable line at once.
  const toggleSelectAll = (checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const l of selectableLines) {
        if (checked) next.add(l.id);
        else next.delete(l.id);
      }
      return next;
    });

  // Lines still awaiting an action: neither posted nor reconciled-and-linked.
  const pendingCount = useMemo(
    () => editLines.filter((l) => !l.posted && !l.matched).length,
    [editLines],
  );

  // Groups of duplicate bank labels among lines still to book. Single-occurrence labels and
  // already handled lines (posted or reconciled) are never grouped.
  const groups = useMemo<LineGroup[]>(() => {
    // The group key a captured line joins, or null when no rule matches.
    const capturedKeyOf = (label: string): string | null => {
      for (const key in captureRules) {
        if ((captureRules[key] ?? []).some((r) => matchesRule(label, r))) return key;
      }
      return null;
    };
    const map = new Map<string, EditLine[]>();
    for (const l of editLines) {
      if (l.posted || l.matched) continue;
      const key = capturedKeyOf(l.bankLabel) ?? groupKey(l.bankLabel);
      if (!key) continue;
      const arr = map.get(key) ?? [];
      arr.push(l);
      map.set(key, arr);
    }
    return Array.from(map.entries())
      .filter(([, members]) => members.length > 1)
      .map(([key, members]) => ({
        key,
        sample: members[0].bankLabel,
        ids: members.map((m) => m.id),
        total: members.reduce((s, m) => s + (Number(m.amount) || 0), 0),
        account: members.every((m) => m.aiSuggestedAccount === members[0].aiSuggestedAccount)
          ? members[0].aiSuggestedAccount
          : '',
        label: members.every((m) => m.aiSuggestedLabel === members[0].aiSuggestedLabel)
          ? members[0].aiSuggestedLabel
          : '',
      }))
      .sort((a, b) => b.ids.length - a.ids.length);
  }, [editLines, captureRules]);

  // Applies one account (reactivating it if hidden) to every member of a group at once.
  const setGroupAccount = (g: LineGroup, number: string) => {
    setEditLines((prev) => {
      setDirty(true);
      return prev.map((l) => (g.ids.includes(l.id) ? { ...l, aiSuggestedAccount: number } : l));
    });
    const acc = (accounts.data ?? []).find((a: any) => a.number === number);
    if (acc && acc.active === false) reactivateAccount.mutate(acc.id);
  };

  const setGroupLabel = (g: LineGroup, label: string) =>
    setEditLines((prev) => {
      setDirty(true);
      return prev.map((l) => (g.ids.includes(l.id) ? { ...l, aiSuggestedLabel: label } : l));
    });

  const toggleMerge = (key: string, merge: boolean) =>
    setMergedKeys((prev) => {
      const next = new Set(prev);
      if (merge) next.add(key);
      else next.delete(key);
      return next;
    });

  const toggleExpand = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Adds a capture rule to a group and keeps it expanded so the pulled-in lines are visible.
  const addCaptureRule = (key: string, mode: CaptureRule['mode'], text: string) => {
    const clean = text.trim();
    if (!clean) return;
    setCaptureRules((prev) => {
      const list = prev[key] ?? [];
      if (list.some((r) => r.mode === mode && normLabel(r.text) === normLabel(clean))) return prev;
      return { ...prev, [key]: [...list, { mode, text: clean }] };
    });
    setExpandedKeys((prev) => new Set(prev).add(key));
  };

  const removeCaptureRule = (key: string, index: number) =>
    setCaptureRules((prev) => {
      const list = (prev[key] ?? []).filter((_, i) => i !== index);
      const next = { ...prev };
      if (list.length) next[key] = list;
      else delete next[key];
      return next;
    });

  // Whole-group selection: checked when every member is picked for creation.
  const groupSelected = (g: LineGroup) => g.ids.every((id) => selectedIds.has(id));
  const toggleGroupSelect = (g: LineGroup, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of g.ids) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  // Ordered render list. In the grouped view recurring labels collapse into one group row
  // (members revealed on demand); single lines and already handled lines stay inline. The
  // detailed view never groups.
  type RenderItem = { kind: 'group'; group: LineGroup } | { kind: 'line'; line: EditLine };
  const renderItems = useMemo<RenderItem[]>(() => {
    if (!grouped) return editLines.map((line) => ({ kind: 'line', line }) as RenderItem);
    const capturedKeyOf = (label: string): string | null => {
      for (const key in captureRules) {
        if ((captureRules[key] ?? []).some((r) => matchesRule(label, r))) return key;
      }
      return null;
    };
    const byKey = new Map(groups.map((g) => [g.key, g]));
    const seen = new Set<string>();
    const items: RenderItem[] = [];
    for (const line of editLines) {
      const key = !line.posted && !line.matched
        ? capturedKeyOf(line.bankLabel) ?? groupKey(line.bankLabel)
        : '';
      const g = key ? byKey.get(key) : undefined;
      if (g) {
        if (seen.has(key)) continue;
        seen.add(key);
        items.push({ kind: 'group', group: g });
      } else {
        items.push({ kind: 'line', line });
      }
    }
    return items;
  }, [grouped, editLines, groups, captureRules]);

  // Auto-merge recurring groups already resolved from memory so a single entry is proposed.
  useEffect(() => {
    if (groups.length === 0) return;
    setMergedKeys((prev) => {
      const next = new Set(prev);
      for (const g of groups) {
        if (next.has(g.key)) continue;
        const members = editLines.filter((l) => g.ids.includes(l.id));
        if (members.length > 1 && g.account && members.every((m) => m.learned)) {
          next.add(g.key);
        }
      }
      return next;
    });
  }, [groups, editLines]);

  // Expand any group that hides a duplicate so the user sees the offending lines right away.
  useEffect(() => {
    if (groups.length === 0 || duplicateIds.size === 0) return;
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      for (const g of groups) {
        if (g.ids.some((id) => duplicateIds.has(id))) next.add(g.key);
      }
      return next;
    });
  }, [groups, duplicateIds]);

  // A standalone line: single-occurrence label, or any line in the detailed view.
  const lineRow = (l: EditLine) => (
    <tr key={l.id} className={l.posted ? 'bg-income-tint/30' : l.matched ? 'bg-brand-tint/40' : signFlipId === l.id ? 'bg-gold-tint/60' : duplicateIds.has(l.id) ? 'bg-gold-tint/40' : ''}>
      <td data-label={t('field.date')} className="whitespace-nowrap text-ink-soft">{dateFmt(l.operationDate)}</td>
      <td data-label={t('field.bankLabel')} className="pr-2">
        <span>{l.bankLabel}</span>
        <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
          {signFlipId === l.id && (
            <span
              className="inline-flex items-center rounded-md bg-gold-tint px-1.5 py-0.5 text-[11px] font-semibold text-gold"
              title={t('imports.verify.signFlipHint')}
            >
              {t('imports.verify.signFlipBadge')}
            </span>
          )}
          {duplicateIds.has(l.id) && (
            <span
              className="inline-flex items-center rounded-md bg-gold-tint px-1.5 py-0.5 text-[11px] font-semibold text-gold"
              title={t('imports.duplicateHint')}
            >
              {t('imports.duplicate')}
            </span>
          )}
          {l.learned && (
            <span
              className="inline-flex items-center rounded-md bg-brand-tint px-1.5 py-0.5 text-[11px] font-semibold text-brand"
              title={t('imports.learnedHint')}
            >
              {t('imports.learned')}
            </span>
          )}
          {l.cashDeposit && (
            <span className="inline-flex items-center rounded-md bg-income-tint px-1.5 py-0.5 text-[11px] font-semibold text-income">
              {t('imports.cashDeposit')}
            </span>
          )}
        </span>
        {duplicateIds.has(l.id) && !l.posted && !l.matched && (
          <button
            type="button"
            onClick={() => deleteLine(l.id)}
            className="btn btn-ghost !px-2 !py-0.5 text-xs text-expense"
            title={t('imports.deleteLineHint')}
          >
            {t('imports.deleteLine')}
          </button>
        )}
      </td>
      <td data-label={t('field.amount')} className="num">
        {l.posted || l.matched ? (
          <div className="flex flex-col items-end gap-1">
            <span>{euro(l.amount)}</span>
            {Number(l.amount) >= 0 ? (
              <span className="inline-flex rounded-md bg-income-tint px-2 py-0.5 text-xs font-medium text-income">
                {t('entries.income')}
              </span>
            ) : (
              <span className="inline-flex rounded-md bg-expense-tint px-2 py-0.5 text-xs font-medium text-expense">
                {t('entries.expense')}
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5 sm:items-end">
          <input
            type="number"
            step="0.01"
            min="0"
            value={Math.abs(Number(l.amount) || 0)}
            onChange={(e) => {
              const abs = Math.abs(Number(e.target.value) || 0);
              patchLine(l.id, { amount: Number(l.amount) < 0 ? -abs : abs });
            }}
            className="input tnum w-28 text-right"
            aria-label={t('field.amount')}
          />
          <div className="inline-flex self-end rounded-lg border border-line bg-surface p-0.5" aria-label={t('entries.flow')}>
            <button
              type="button"
              onClick={() => patchLine(l.id, { amount: Math.abs(Number(l.amount) || 0) })}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                Number(l.amount) >= 0
                  ? 'bg-income-tint text-income'
                  : 'text-ink-muted hover:text-ink'
              }`}
            >
              {t('entries.income')}
            </button>
            <button
              type="button"
              onClick={() => patchLine(l.id, { amount: -Math.abs(Number(l.amount) || 0) })}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                Number(l.amount) < 0
                  ? 'bg-expense-tint text-expense'
                  : 'text-ink-muted hover:text-ink'
              }`}
            >
              {t('entries.expense')}
            </button>
          </div>
          </div>
        )}
      </td>
      {l.posted ? (
        <td data-label={t('imports.allocation')} className="cell-block">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-md bg-income-tint px-2 py-0.5 text-xs font-semibold text-income">
              {l.matched ? t('imports.reconciled') : t('imports.posted')}
            </span>
            {!l.matched && (
              <span className="text-sm text-ink-soft">
                {l.aiSuggestedAccount}
                {accountLabelOf(l.aiSuggestedAccount) ? ` · ${accountLabelOf(l.aiSuggestedAccount)}` : ''}
              </span>
            )}
            {l.matched && l.matchSummary && (
              <span className="text-sm text-ink-soft">{l.matchSummary}</span>
            )}
          </div>
        </td>
      ) : l.matched ? (
        <td data-label={t('imports.allocation')} className="cell-block">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-md bg-brand-tint px-2 py-0.5 text-xs font-semibold text-brand">
              {t('imports.reconcileProposed')}
            </span>
            <span className="text-sm text-ink-soft">{l.matchSummary}</span>
            {l.matchScore != null && (
              <span className="tnum text-xs text-ink-muted">
                {Math.round(Number(l.matchScore) * 100)}%
              </span>
            )}
            <button
              type="button"
              onClick={() => patchLine(l.id, { matched: false })}
              className="btn btn-ghost !px-2 !py-0.5 text-xs"
            >
              {t('imports.reconcileUnlink')}
            </button>
            <button
              type="button"
              onClick={() => deleteLine(l.id)}
              className="btn btn-ghost !px-2 !py-0.5 text-xs text-expense"
              title={t('imports.deleteLineHint')}
            >
              {t('imports.deleteLine')}
            </button>
          </div>
        </td>
      ) : (
        <td data-label={t('imports.allocation')} className="sm:min-w-[18rem]">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <input
                list="import-account-options"
                value={l.aiSuggestedAccount}
                onChange={(e) => chooseAccount(l.id, e.target.value)}
                className="input tnum w-28 shrink-0"
                aria-label={t('imports.suggestedAccount')}
                title={t('imports.suggestedAccount')}
              />
              {Number(l.amount) > 0 && (
                <select
                  value={l.paymentMode}
                  onChange={(e) => changePaymentMode(l, e.target.value)}
                  className="select min-w-0 flex-1 !py-1.5 text-sm"
                  aria-label={t('imports.mode')}
                  title={t('imports.mode')}
                >
                  <option value="">{t('imports.paymentModeNone')}</option>
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {t(`imports.paymentModes.${m}`)}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <input
              value={l.aiSuggestedLabel}
              onChange={(e) => patchLine(l.id, { aiSuggestedLabel: e.target.value })}
              className="input w-full"
              aria-label={t('field.label')}
              placeholder={t('field.label')}
            />
            {((l.aiSuggestedAccount && accountLabelOf(l.aiSuggestedAccount)) || l.aiConfidence != null) && (
              <div className="flex items-center justify-between gap-2 text-xs text-ink-muted">
                <span className="truncate">
                  {l.aiSuggestedAccount ? accountLabelOf(l.aiSuggestedAccount) : ''}
                </span>
                {l.aiConfidence != null && (
                  <span className="tnum shrink-0">
                    {t('imports.confidence')} {Math.round(Number(l.aiConfidence) * 100)}%
                  </span>
                )}
              </div>
            )}
            {Number(l.amount) > 0 && l.paymentMode === 'CASH_DEPOSIT' && (
              <label className="flex items-center gap-1.5 text-xs text-income">
                <input
                  type="checkbox"
                  checked={l.cashDeposit}
                  onChange={(e) => patchLine(l.id, { cashDeposit: e.target.checked })}
                  className="h-3.5 w-3.5 accent-[var(--brand)]"
                />
                {t('imports.createCashDeposit')}
              </label>
            )}
          </div>
        </td>
      )}
      <td data-label={t('imports.createEntry')} className="text-center">
        {l.posted ? (
          <span className="text-income" title={t('imports.posted')}>✓</span>
        ) : l.matched ? (
          <span className="text-xs text-ink-muted" title={t('imports.reconcileProposed')}>
            ⇄
          </span>
        ) : (
          <input
            type="checkbox"
            checked={selectedIds.has(l.id)}
            onChange={(e) => toggleSelect(l.id, e.target.checked)}
            className="h-4 w-4 accent-[var(--brand)]"
            title={t('imports.createEntryHint')}
          />
        )}
      </td>
    </tr>
  );

  // A collapsed member line, shown only when its group is expanded. It inherits the group's
  // account/label and only exposes its individual inclusion toggle.
  const memberRow = (l: EditLine) => (
    <tr key={l.id} className={duplicateIds.has(l.id) ? 'bg-gold-tint/40 text-sm' : 'bg-surface-muted/40 text-sm'}>
      <td data-label={t('field.date')} className="whitespace-nowrap pl-6 text-ink-soft">{dateFmt(l.operationDate)}</td>
      <td data-label={t('field.bankLabel')} className="pl-6 pr-2 text-ink-soft">
        {l.bankLabel}
        {duplicateIds.has(l.id) && (
          <>
            <span
              className="ml-2 inline-flex items-center rounded-md bg-gold-tint px-1.5 py-0.5 text-[11px] font-semibold text-gold"
              title={t('imports.duplicateHint')}
            >
              {t('imports.duplicate')}
            </span>
            <button
              type="button"
              onClick={() => deleteLine(l.id)}
              className="btn btn-ghost !ml-1 !px-2 !py-0.5 text-xs text-expense"
              title={t('imports.deleteLineHint')}
            >
              {t('imports.deleteLine')}
            </button>
          </>
        )}
      </td>
      <td data-label={t('field.amount')} className="num">
        <div className="flex flex-col items-end gap-1">
          <span>{euro(l.amount)}</span>
          {Number(l.amount) >= 0 ? (
            <span className="inline-flex rounded-md bg-income-tint px-2 py-0.5 text-xs font-medium text-income">
              {t('entries.income')}
            </span>
          ) : (
            <span className="inline-flex rounded-md bg-expense-tint px-2 py-0.5 text-xs font-medium text-expense">
              {t('entries.expense')}
            </span>
          )}
        </div>
      </td>
      <td className="text-xs italic text-ink-muted">
        {t('imports.inheritsGroup')}
      </td>
      <td data-label={t('imports.createEntry')} className="text-center">
        <input
          type="checkbox"
          checked={selectedIds.has(l.id)}
          onChange={(e) => toggleSelect(l.id, e.target.checked)}
          className="h-4 w-4 accent-[var(--brand)]"
          title={t('imports.createEntryHint')}
        />
      </td>
    </tr>
  );

  // A grouped recurring label: one editable row driving all its member lines.
  const groupRow = (g: LineGroup) => {
    const expanded = expandedKeys.has(g.key);
    return (
      <tr key={`g-${g.key}`} className="bg-brand-tint/20">
        <td data-label={t('field.date')} className="whitespace-nowrap">
          <button
            type="button"
            onClick={() => toggleExpand(g.key)}
            className="btn btn-ghost !px-2 !py-0.5 tnum text-sm"
            title={expanded ? t('imports.collapse') : t('imports.expand')}
          >
            {expanded ? '−' : '+'} {g.ids.length}
          </button>
        </td>
        <td data-label={t('field.bankLabel')} className="pr-2">
          <span className="font-medium">{g.sample}</span>
          <span className="ml-2 inline-flex items-center rounded-md bg-brand-tint px-1.5 py-0.5 text-[11px] font-semibold text-brand">
            {t('imports.linesCount', { count: g.ids.length })}
          </span>
          {g.ids.some((id) => duplicateIds.has(id)) && (
            <span
              className="ml-2 inline-flex items-center rounded-md bg-gold-tint px-1.5 py-0.5 text-[11px] font-semibold text-gold"
              title={t('imports.duplicateHint')}
            >
              {t('imports.duplicate')}
            </span>
          )}
        </td>
        <td data-label={t('field.amount')} className="num">
          <div className="flex flex-col items-end gap-1">
            <span>{euro(g.total)}</span>
            {g.total >= 0 ? (
              <span className="inline-flex rounded-md bg-income-tint px-2 py-0.5 text-xs font-medium text-income">
                {t('entries.income')}
              </span>
            ) : (
              <span className="inline-flex rounded-md bg-expense-tint px-2 py-0.5 text-xs font-medium text-expense">
                {t('entries.expense')}
              </span>
            )}
          </div>
        </td>
        <td data-label={t('imports.allocation')} className="sm:min-w-[18rem]">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <input
                list="import-account-options"
                value={g.account}
                onChange={(e) => setGroupAccount(g, e.target.value)}
                className="input tnum w-28 shrink-0"
                aria-label={t('imports.suggestedAccount')}
                title={t('imports.suggestedAccount')}
              />
              <label className="flex items-center gap-1.5 text-xs text-ink-soft">
                <input
                  type="checkbox"
                  checked={mergedKeys.has(g.key)}
                  onChange={(e) => toggleMerge(g.key, e.target.checked)}
                  className="h-4 w-4 accent-[var(--brand)]"
                />
                {t('imports.mergeOne')}
              </label>
            </div>
            <input
              value={g.label}
              placeholder={g.sample}
              onChange={(e) => setGroupLabel(g, e.target.value)}
              className="input w-full"
              aria-label={t('field.label')}
            />
            {g.account && accountLabelOf(g.account) && (
              <span className="truncate text-xs text-ink-muted">{accountLabelOf(g.account)}</span>
            )}
          </div>
        </td>
        <td data-label={t('imports.createEntry')} className="text-center">
          <input
            type="checkbox"
            checked={groupSelected(g)}
            onChange={(e) => toggleGroupSelect(g, e.target.checked)}
            className="h-4 w-4 accent-[var(--brand)]"
            title={t('imports.createEntryHint')}
          />
        </td>
      </tr>
    );
  };

  return (
    <div>
      <header className="mb-6">
        <p className="eyebrow">{t('nav.imports')}</p>
        <h1 className="page-title mt-1">{t('imports.title')}</h1>
      </header>

      <div className="card mb-6 p-5 sm:p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-12 sm:items-end">
          <div className="sm:col-span-4">
            <label className="field-label">{t('imports.bankAccount')}</label>
            {bankAccounts.length > 1 ? (
              <select
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value)}
                className="select"
              >
                {bankAccounts.map((a: any) => (
                  <option key={a.id} value={a.accountNumber}>
                    {a.name} · {a.accountNumber}
                  </option>
                ))}
              </select>
            ) : (
              <p className="input flex items-center bg-surface-raised tnum">
                {bankAccounts.length === 1
                  ? `${bankAccounts[0].name} · ${bankAccounts[0].accountNumber}`
                  : '-'}
              </p>
            )}
          </div>

          <div className="sm:col-span-5">
            <label className="field-label">{t('imports.file')}</label>
            <label className="input flex cursor-pointer items-center gap-2 overflow-hidden hover:border-brand/50">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-brand-tint text-brand">
                {file ? <FileText size={14} /> : <UploadCloud size={14} />}
              </span>
              <span className={`truncate ${file ? 'text-ink' : 'text-ink-muted'}`}>
                {file ? file.name : t('imports.chooseFile')}
              </span>
              <input
                type="file"
                accept=".csv,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="hidden"
              />
            </label>
          </div>

          <div className="sm:col-span-3">
            <button
              disabled={!file || !bankJournalId || analyzing}
              onClick={() => file && start(file, bankJournalId, bankAccount || defaultBankNumber || undefined)}
              className="btn btn-primary w-full"
            >
              {analyzing ? (
                <>
                  <Spinner /> {t('imports.analyzing')}
                </>
              ) : (
                <>
                  <UploadCloud size={16} /> {t('imports.import')}
                </>
              )}
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-ink-muted">{t('imports.fileHint')}</p>
      </div>

      {analyzing && (
        <div className="reveal card mb-6 flex items-center gap-4 p-5">
          <span
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-tint text-brand"
            aria-hidden
          >
            <Loader2 className="animate-spin" size={22} />
          </span>
          <div className="min-w-0">
            <p className="font-display text-base font-semibold">{t('imports.analyzingTitle')}</p>
            <p className="truncate text-sm text-ink-muted">{job?.fileName}</p>
          </div>
          <p className="ml-auto hidden max-w-xs text-sm text-ink-soft sm:block">
            {t('imports.analyzingHint')}
          </p>
        </div>
      )}

      {job?.status === 'error' && (
        <div className="card mb-6 flex items-center gap-4 border-expense/50 p-5">
          <div className="min-w-0">
            <p className="font-display text-base font-semibold text-expense">
              {t('imports.failed')}
            </p>
            <p className="truncate text-sm text-ink-muted">{job.error}</p>
          </div>
          <button onClick={() => clear()} className="btn btn-secondary ml-auto">
            {t('common.close')}
          </button>
        </div>
      )}

      {importId && (
        <div className="reveal card mb-6 p-5">
          {detail.data?.import?.errorMessage && (
            <div className="mb-4 rounded-xl border border-expense/40 p-4">
              <p className="font-display text-sm font-semibold text-expense">
                {t('imports.parseErrorTitle')}
              </p>
              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-ink-muted">
                {detail.data.import.errorMessage}
              </p>
            </div>
          )}
          {detail.data?.verification && <VerificationPanel v={detail.data.verification} />}
          <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
            {isSuperAdmin && detail.data?.import && (
              <span className="pill pill-neutral" title={t('imports.bankProfileHint')}>
                {detail.data.import.bankProfileCode
                  ? t('imports.bankProfile', {
                      code: detail.data.import.bankProfileCode,
                      version: detail.data.import.bankProfileVersion,
                    })
                  : t('imports.bankProfileGeneric')}
              </span>
            )}
            {isSuperAdmin && aiCost.data && (
              <span className="pill pill-neutral mr-auto" title={t('imports.aiCostHint')}>
                {t('imports.aiCost')}{' '}
                {new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 4,
                }).format(Number.parseFloat(aiCost.data.costUsd) || 0)}
              </span>
            )}
            {alreadyValidated ? (
              <span className="pill pill-posted">{t('imports.alreadyValidated')}</span>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {pendingCount > 0 ? (
                  <span className="pill pill-neutral mr-auto">
                    {t('imports.pendingLines', { count: pendingCount })}
                  </span>
                ) : (
                  <span className="pill pill-posted mr-auto">{t('imports.allSettled')}</span>
                )}
                {dirty && <span className="pill pill-neutral">{t('imports.unsaved')}</span>}
                <button
                  onClick={() => setShowAllAccounts((v) => !v)}
                  className="btn btn-ghost"
                  title={t('imports.showAllAccountsHint')}
                >
                  {showAllAccounts ? t('imports.activeAccountsOnly') : t('imports.showAllAccounts')}
                </button>
                <button
                  onClick={() => setGrouped((v) => !v)}
                  className={grouped ? 'btn btn-secondary' : 'btn btn-ghost'}
                >
                  {grouped ? t('imports.ungroup') : t('imports.group')}
                </button>
                <button
                  onClick={() => saveLines.mutate()}
                  disabled={saveLines.isPending || editLines.length === 0}
                  className="btn btn-secondary"
                >
                  {saveLines.isPending ? t('common.saving') : t('common.save')}
                </button>
                <label
                  className="flex items-center gap-1.5 text-sm text-ink-soft"
                  title={t('imports.autoValidateHint')}
                >
                  <input
                    type="checkbox"
                    checked={autoValidate}
                    onChange={(e) => setAutoValidate(e.target.checked)}
                  />
                  {t('imports.autoValidate')}
                </label>
                <label
                  className="flex items-center gap-1.5 text-sm text-ink-soft"
                  title={t('imports.autoReconcileHint')}
                >
                  <input
                    type="checkbox"
                    checked={autoReconcile}
                    onChange={(e) => setAutoReconcile(e.target.checked)}
                  />
                  {t('imports.autoReconcile')}
                </label>
                <button
                  onClick={() => validate.mutate()}
                  disabled={validate.isPending || (selectedIds.size === 0 && pendingCount === 0)}
                  className="btn btn-primary"
                  title={t('imports.generateEntriesHint')}
                >
                  {validate.isPending ? t('imports.generating') : t('imports.generateEntries')}
                </button>
              </div>
            )}
          </div>
          {saveError && (
            <div className="mb-4 rounded-xl border border-expense/40 bg-expense-tint/40 p-3 text-sm text-expense">
              {saveError}
            </div>
          )}

          <div className="overflow-x-auto">
          <table className="table sm:[&_td]:!px-2 sm:[&_th]:!px-2">
            <thead>
              <tr>
                <th>{t('field.date')}</th>
                <th>{t('field.bankLabel')}</th>
                <th className="num">{t('field.amount')}</th>
                <th>{t('imports.allocation')}</th>
                <th className="text-center">
                  <span className="flex flex-col items-center gap-1">
                    <span>{t('imports.createEntry')}</span>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      className="h-4 w-4 accent-[var(--brand)]"
                      title={t('imports.selectAll')}
                    />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {renderItems.map((it) =>
                it.kind === 'group' ? (
                  <Fragment key={`g-${it.group.key}`}>
                    {groupRow(it.group)}
                    {expandedKeys.has(it.group.key) && (
                      <tr key={`r-${it.group.key}`} className="bg-brand-tint/10">
                        <td colSpan={5} className="px-3 py-2">
                          <CaptureRuleEditor
                            rules={captureRules[it.group.key] ?? []}
                            onAdd={(mode, text) => addCaptureRule(it.group.key, mode, text)}
                            onRemove={(i) => removeCaptureRule(it.group.key, i)}
                          />
                        </td>
                      </tr>
                    )}
                    {expandedKeys.has(it.group.key) &&
                      editLines
                        .filter((l) => it.group.ids.includes(l.id))
                        .map((l) => memberRow(l))}
                  </Fragment>
                ) : (
                  lineRow(it.line)
                ),
              )}
            </tbody>
          </table>
          </div>
          <datalist id="import-account-options">
            {visibleAccounts.map((a: any) => (
              <option key={a.number} value={a.number}>
                {a.number} - {a.label}
                {a.active === false ? ` (${t('imports.inactiveAccount')})` : ''}
              </option>
            ))}
          </datalist>
        </div>
      )}

      <div className="card p-5">
        <h2 className="mb-2 font-display text-lg font-semibold">{t('imports.history')}</h2>
        <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>{t('field.file')}</th>
              <th>{t('field.lines')}</th>
              <th>{t('imports.entriesColumn')}</th>
              <th>{t('field.status')}</th>
              <th className="text-right">{t('field.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {(history.data ?? []).map((h: any) => (
              <tr
                key={h.id}
                className="cursor-pointer"
                onClick={() => setImportId(h.id)}
              >
                <td data-label={t('field.file')} className="font-medium">{h.fileName}</td>
                <td data-label={t('field.lines')} className="tnum text-ink-soft">{h.totalLines}</td>
                <td data-label={t('imports.entriesColumn')} className="tnum text-ink-soft">{h.createdEntries}</td>
                <td data-label={t('field.status')} className="text-ink-soft">{t(`imports.status.${h.status}`, { defaultValue: h.status })}</td>
                <td onClick={(e) => e.stopPropagation()} className="cell-actions text-right">
                  <button
                    onClick={() => openStatementFile(h.id)}
                    className="btn btn-ghost !px-2 !py-1"
                  >
                    {t('imports.viewFile')}
                  </button>
                  <button
                    onClick={() => askDelete(h.id, h.fileName)}
                    className="btn btn-ghost !px-2 !py-1 text-expense"
                  >
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-expense" />
              <div>
                <h3 className="text-lg font-semibold text-ink">
                  {t('imports.delete.title')}
                </h3>
                <p className="mt-1 text-sm text-ink-soft">
                  {t('imports.delete.file', { file: deleteTarget.fileName })}
                </p>
              </div>
            </div>

            <p className="mt-4 text-sm text-ink">
              {deleteTarget.count === null
                ? t('imports.delete.loading')
                : deleteTarget.count > 0
                  ? t('imports.delete.withEntries', { count: deleteTarget.count })
                  : t('imports.delete.noEntries')}
            </p>

            <div className="mt-6 flex flex-col gap-2">
              <button
                onClick={() =>
                  removeImport.mutate({ id: deleteTarget.id, deleteEntries: false })
                }
                disabled={removeImport.isPending}
                className="btn btn-primary"
              >
                {deleteTarget.count && deleteTarget.count > 0
                  ? t('imports.delete.keepEntries')
                  : t('imports.delete.confirm')}
              </button>
              {deleteTarget.count !== null && deleteTarget.count > 0 && (
                <button
                  onClick={() =>
                    removeImport.mutate({ id: deleteTarget.id, deleteEntries: true })
                  }
                  disabled={removeImport.isPending}
                  className="btn btn-ghost text-expense"
                >
                  {t('imports.delete.alsoEntries', { count: deleteTarget.count })}
                </button>
              )}
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={removeImport.isPending}
                className="btn btn-ghost"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
